/* eslint-disable jsx-a11y/control-has-associated-label,jsx-a11y/interactive-supports-focus,jsx-a11y/click-events-have-key-events */
import styled from "@emotion/styled";
import React, { useState, ReactNode } from "react";
import { compassColors } from "src/utils/styling";
import ConfettiTadaStatic from "src/components/banner/confetti_data_static.svg";
import { useDisableScroll } from "src/hooks/scroll";
import { Modal } from "src/design-system/components/Modal/Modal";
import { Text } from "@components/core/Text";
import { useGlobalEventListener } from "src/hooks/dom";
import { resetFormElement } from "src/design-system/styles/resets";
import { fontP4Medium } from "src/design-system/styles/fonts";

export const AnimationContainer = styled.div`
  height: 50px;
  width: 50px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

export const BannerContainer = styled.div<{ fullWidth?: boolean }>`
  height: 60px;
  background-color: ${compassColors.sky};
  display: flex;
  justify-content: center;
  padding: 16px 0;
  ${({ fullWidth }) =>
    fullWidth
      ? `
        width: 100%;
      `
      : `
        margin-left: calc(50% - 50vw);
        margin-right: calc(50% - 50vw);
      `};
`;

export const ColumnContainer = styled.div`
  display: flex;
  flex-direction: row;
  max-width: 600px;
  margin: 0 20px;
  align-items: center;
`;

export const HeadlineContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0 10px;
`;

export const Headline = styled.span`
  ${fontP4Medium};
  strong {
    font-weight: 700;
  }
`;

export const StaticImg = styled.img`
  height: 40px;
  width: 40px;
  margin-top: -6px;
  margin-left: -1px;
`;

const ContentWrapper = styled.div`
  padding: 0 16px;
  overflow: scroll;
  max-height: 300px;
`;

const OfferDetailsButton = styled.button`
  ${resetFormElement};
  text-decoration: underline;
  border: none;
  background: none;
  display: inline;
  padding: 0;
  outline: none;
  box-shadow: none;
`;

interface BannerProps {
  fullWidth?: boolean;
  headline: string;
  headlineValues?: Record<string, any>;
  modalContent: ReactNode;
}

function OfferDetailsModal({
  onClose,
  children,
}: {
  onClose: () => void;
  children: ReactNode;
}) {
  useDisableScroll();

  useGlobalEventListener("keydown", (e: KeyboardEvent) => {
    if (e.key === "Escape") {
      onClose();
    }
  });

  return (
    <Modal onClickOutside={onClose} onClose={onClose} includeCloseButton>
      <ContentWrapper>{children}</ContentWrapper>
    </Modal>
  );
}

export function Banner({
  fullWidth = true,
  headline,
  headlineValues,
  modalContent,
}: BannerProps) {
  const [modalOpen, setModalOpen] = useState(false);

  const openModal = (event) => {
    event.preventDefault();
    setModalOpen(true);
  };

  return (
    <BannerContainer fullWidth={fullWidth} data-testid="banner-container">
      {modalOpen && (
        <OfferDetailsModal onClose={() => setModalOpen(false)}>
          {modalContent}
        </OfferDetailsModal>
      )}

      <ColumnContainer>
        <AnimationContainer>
          <StaticImg src={ConfettiTadaStatic} />
        </AnimationContainer>
        <HeadlineContainer>
          <Headline>
            <Text
              i18nKey={headline}
              components={{
                ModalOpenLink: (
                  <OfferDetailsButton role="button" onClick={openModal} />
                ),
              }}
              values={headlineValues}
            />
          </Headline>
        </HeadlineContainer>
      </ColumnContainer>
    </BannerContainer>
  );
}
